/* eslint-disable import/prefer-default-export */
import { useMemo, useState } from 'react';

import type { InstrumentType } from '../../../../context/TranscriptionDialogContext/TranscriptionDialogContext';

export const STEPS = {
  CHOOSE_INSTRUMENT: 'CHOOSE_INSTRUMENT',
  SOLO: 'SOLO',
  ADVANCED: 'ADVANCED',
  COMPOSER_TITLE: 'COMPOSER_TITLE',
  PEDAL: 'PEDAL',
  LYRICS: 'LYRICS',
  PIANO_STYLE: 'PIANO_STYLE',
  GUITAR_STYLE: 'GUITAR_STYLE',
  TUNING: 'TUNING',
  CAPO: 'CAPO',
  DRUMSET: 'DRUMSET',
  INSTRUMENT_ORDER: 'INSTRUMENT_ORDER',
  SPECIFY_INSTRUMENT: 'SPECIFY_INSTRUMENT',
} as const;

export type Step = keyof typeof STEPS;

export const STEP_TITLE: Record<Step, string> = {
  [STEPS.CHOOSE_INSTRUMENT]: 'home.input.dialog.choose_instrument.title',
  [STEPS.SOLO]: 'home.input.dialog.soloRecording.title',
  [STEPS.ADVANCED]: 'home.input.dialog.advanced.title',
  [STEPS.COMPOSER_TITLE]: 'home.input.dialog.title_composer.title',
  [STEPS.PEDAL]: 'home.input.dialog.pedal.title',
  [STEPS.PIANO_STYLE]: 'home.input.dialog.style_piano.title',
  [STEPS.GUITAR_STYLE]: 'home.input.dialog.style_guitar.title',
  [STEPS.CAPO]: 'home.input.dialog.capo.title',
  [STEPS.TUNING]: 'home.input.dialog.tuning.title',
  [STEPS.LYRICS]: 'home.input.dialog.lyrics.title',
  [STEPS.DRUMSET]: 'home.input.dialog.drumset.title',
  [STEPS.INSTRUMENT_ORDER]: 'home.input.dialog.instrument_order.title',
  [STEPS.SPECIFY_INSTRUMENT]: 'home.input.dialog.specify_instrument.title',
};

export const HELPER_TEXT: Record<Step, string> = {
  [STEPS.CHOOSE_INSTRUMENT]: 'home.input.dialog.choose_instrument.help',
  [STEPS.SOLO]: 'home.input.dialog.soloRecording.help',
  [STEPS.ADVANCED]: 'home.input.dialog.advanced.help',
  [STEPS.COMPOSER_TITLE]: 'home.input.dialog.title_composer.help',
  [STEPS.PEDAL]: 'home.input.dialog.pedal.help',
  [STEPS.PIANO_STYLE]: 'home.input.dialog.style_piano.help',
  [STEPS.GUITAR_STYLE]: 'home.input.dialog.style_guitar.help',
  [STEPS.CAPO]: 'home.input.dialog.capo.help',
  [STEPS.TUNING]: 'home.input.dialog.tuning.help',
  [STEPS.LYRICS]: 'home.input.dialog.lyrics.help',
  [STEPS.DRUMSET]: 'home.input.dialog.drumset.help',
  [STEPS.INSTRUMENT_ORDER]: 'home.input.dialog.instrument_order.help',
  [STEPS.SPECIFY_INSTRUMENT]: 'home.input.dialog.specify_instrument.help',
};

const COMMON_STEPS: Step[] = [STEPS.ADVANCED, STEPS.COMPOSER_TITLE];
const RETRANSCRIBE_STEPS: Step[] = [STEPS.ADVANCED];

interface StepConfig {
  solo: Step[];
  multi: Step[];
}

const INSTRUMENT_CONFIGS: Partial<Record<InstrumentType, StepConfig>> = {
  piano: {
    solo: [STEPS.PEDAL, STEPS.PIANO_STYLE],
    multi: [STEPS.PIANO_STYLE],
  },
  acoustic_guitar: {
    solo: [STEPS.GUITAR_STYLE, STEPS.TUNING, STEPS.CAPO],
    multi: [STEPS.TUNING, STEPS.CAPO],
  },
  electric_guitar: {
    solo: [STEPS.GUITAR_STYLE, STEPS.TUNING, STEPS.CAPO],
    multi: [STEPS.TUNING, STEPS.CAPO],
  },
  guitar: {
    solo: [STEPS.GUITAR_STYLE, STEPS.TUNING, STEPS.CAPO],
    multi: [STEPS.TUNING, STEPS.CAPO],
  },
  drums: {
    solo: [STEPS.DRUMSET],
    multi: [STEPS.DRUMSET],
  },
  vocals: {
    solo: [STEPS.LYRICS],
    multi: [STEPS.LYRICS],
  },
  bass: {
    solo: [STEPS.TUNING],
    multi: [STEPS.TUNING],
  },
  wind: {
    solo: [STEPS.SPECIFY_INSTRUMENT],
    multi: [STEPS.SPECIFY_INSTRUMENT],
  },
  strings: {
    solo: [STEPS.SPECIFY_INSTRUMENT],
    multi: [STEPS.SPECIFY_INSTRUMENT],
  },
};

export const getStepConfigInstruments = (
  instruments: InstrumentType[],
  isSolo: boolean,
  isRetranscribe?: boolean,
): Step[] => {
  const finalSteps = isRetranscribe ? RETRANSCRIBE_STEPS : COMMON_STEPS;

  if (instruments.length > 1) {
    return [
      STEPS.CHOOSE_INSTRUMENT,
      ...(instruments.includes('vocals') ? [STEPS.LYRICS] : []),
      ...finalSteps,
    ];
  }
  const instrumentConfig = INSTRUMENT_CONFIGS[instruments[0]];
  if (!instrumentConfig) {
    return [STEPS.CHOOSE_INSTRUMENT, STEPS.SOLO, ...finalSteps];
  }

  const mandatorySteps = isSolo ? instrumentConfig.solo : instrumentConfig.multi;
  return [STEPS.CHOOSE_INSTRUMENT, STEPS.SOLO, ...mandatorySteps, ...finalSteps];
};

export const useSteps = () => {
  const [stepList, setStepList] = useState<Step[]>([
    STEPS.CHOOSE_INSTRUMENT,
    STEPS.SOLO,
    ...COMMON_STEPS,
  ]);

  const updateStepList = (
    instruments: InstrumentType[],
    isSolo: boolean,
    isRetranscribe?: boolean,
  ) => {
    setStepList(getStepConfigInstruments(instruments, isSolo, isRetranscribe));
  };

  return useMemo(
    () => ({
      stepList,
      setStepList: updateStepList,
    }),
    [stepList],
  );
};

export const COMPOSER_BLACKLIST = ['Bodo Wartke'];
